import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../Utils/axiosInstance";
import storage from 'redux-persist/lib/storage/session';
import { persistReducer } from 'redux-persist';
import {
  clearGuestCheckout,
  deleteCookie,
  getCookieValue,
  getCurrentCheckoutToken,
  getStorage,
  isGuestCheckoutForToken,
  persistGuestCheckout,
} from "../Utils/utils";
import {
  openAddressComponent,
  removeUpdatedShopperDetails,
  updateSavedAddressData,
} from "./PaymentSessionDetailsSlice";
import { removeAllSavedAddress } from "./SavedAddressSlice";
import { setSavedInstruments } from "./SavedInstrumentSlice";

const baseUrl = process.env.REACT_APP_BASE_URL;

const persistConfig = {
  key: 'shopperInfo',
  storage,
  // Guest state is derived per checkout token from localStorage (isGuestCheckoutForToken);
  // persisting it per tab restores a stale value and hides the login screen.
  blacklist: ['guestCheckout'],
};

export const loginOtpInitiation = createAsyncThunk(
  "shopperInfo/login",
  async ({ mobileNumber, keepSignedIn, checkoutToken }, { rejectWithValue }) => {
    try {
      const requestData = {
        shopperRef: mobileNumber,
        refType: "Mobile",
        checkoutToken,
      };
      if (keepSignedIn) {
        requestData.extendedLogin = true;
      }

      const response = await axiosInstance.post(
        `${baseUrl}/checkout/sessions/${checkoutToken}/shoppers/login/initiate`,
        requestData
      );
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response);
    }
  }
);

export const loginOtpVerification = createAsyncThunk(
  "shopperInfo/login/otp",
  async ({ mobileNumber, otp, checkoutToken }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(
        `${baseUrl}/checkout/sessions/${checkoutToken}/shoppers/login`,
        {
          shopperRef: mobileNumber,
          refType: "Mobile",
          token: otp,
        }
      );
      deleteCookie("shopper_token")
      return response.data;
    } catch (err) {
      return rejectWithValue(err?.response);
    }
  }
);

const initialState = {
  shopperinfo: {
    shopperRef: "",
    sessionToken: "",
  },
  showOtpModal: false,
  otpInitiated: false,
  otpVerificationInitiated: false,
  loginOtpInitiationErrorMsg: "",
  loginOtpVerificationErrMsg: "",
  isLoggedIn: getCookieValue('shopper_token') || localStorage.getItem("sessionToken") ? true : false,
  keepSignedIn: false,
  guestCheckout: isGuestCheckoutForToken(getCurrentCheckoutToken()),
};

// ---------------------------------------------------------------------------
// Login gate. This file is the only place that decides whether the login screen
// shows. Everything else reads it through the selectors at the bottom.
//
//   login enabled   = sessionDetails.configs.additionalFieldSets has SHOPPER_LOGIN
//   login screen    = enabled && !isLoggedIn && !guestCheckout
//   cookie bypass   = a shopper_token cookie is present: logged in no matter what
//                     SHOPPER_LOGIN says, and no logout button
//
// Guest checkout is remembered per checkout token (localStorage), so a new token
// shows the login screen again.
// ---------------------------------------------------------------------------
const isLoginEnabled = (sessionDetails) =>
  !!sessionDetails?.configs?.additionalFieldSets?.includes("SHOPPER_LOGIN");

const hasShopperToken = () =>
  !!(getCookieValue("shopper_token") || localStorage.getItem("sessionToken"));

// Removes everything that identifies a logged-in shopper from the browser.
const clearShopperStorage = () => {
  localStorage.removeItem("sessionToken");
  localStorage.removeItem("shopperRef");
  localStorage.removeItem("isUserLoggedIn");
  sessionStorage.removeItem("latestSavedAddress");
  sessionStorage.removeItem("paymentMethods");
  deleteCookie("shopper_token");
};

// Guest only if it was chosen for this very token; with login off the shopper is a guest.
const resolveGuestCheckout = (loginEnabled, hasToken, sessionDetails, hasCookieToken) => {
  if (hasCookieToken) {
    clearGuestCheckout();
    return false;
  }
  if (!loginEnabled) return true;
  if (hasToken) {
    clearGuestCheckout();
    return false;
  }
  return isGuestCheckoutForToken(sessionDetails?.token);
};

const shopperInfoSlice = createSlice({
  name: "shopperInfo",
  initialState,
  reducers: {
    // Re-derives login/guest/shopper state from storage + session config.
    // Called whenever session details load or the shopper cookie changes.
    syncLoginState(state, action) {
      const sessionDetails = action.payload;
      const loginEnabled = isLoginEnabled(sessionDetails);
      const cookieToken = getCookieValue("shopper_token");
      const storedToken = localStorage.getItem("sessionToken");
      const shopperToken = cookieToken || storedToken;
      // A shopper_token cookie bypasses login entirely: SHOPPER_LOGIN is not consulted.
      if (cookieToken) {
        localStorage.removeItem("isUserLoggedIn");
      }

      // Login disabled on the session: end any shopper login so checkout falls back to
      // the session's own address, and login shows again if it is re-enabled.
      // Never for a cookie login, which is independent of the session config.
      if (!cookieToken && !loginEnabled && sessionDetails?.configs && shopperToken) {
        clearShopperStorage();
      }

      state.isLoggedIn = !!cookieToken || (loginEnabled && !!shopperToken);
      state.guestCheckout = resolveGuestCheckout(loginEnabled, !!shopperToken, sessionDetails, !!cookieToken);

      // A shopper ref is only kept for a logged-in shopper (a stored token).
      if (!storedToken || (loginEnabled && state.guestCheckout)) {
        localStorage.removeItem("shopperRef");
      }
      const shopperRef = storedToken
        ? localStorage.getItem("shopperRef")
        : sessionDetails?.paymentDetails?.shopper?.uniqueReference || "";
      if (shopperRef) {
        localStorage.setItem("shopperRef", shopperRef);
      }

      state.shopperinfo = {
        shopperRef: loginEnabled || cookieToken ? shopperRef : "",
        sessionToken: loginEnabled || cookieToken ? shopperToken || "" : "",
      };
      state.showOtpModal = false;
    },
    resetOtpModal(state) {
      state.showOtpModal = false;
    },
    resetOtpInitiationError(state) {
      state.loginOtpInitiationErrorMsg = "";
    },
    resetOtpValidationError(state) {
      state.loginOtpVerificationErrMsg = "";
    },
    resetShopperInfo(state) {
      state.isLoggedIn = false;
      state.guestCheckout = false;
      state.shopperinfo = {};
      clearShopperStorage();
      clearGuestCheckout();
    },
    handleKeepSignIn(state, action) {
      state.keepSignedIn = action.payload;
    },
    // User picked "continue as guest" on the login screen.
    handleGuestCheckout(state, action) {
      state.guestCheckout = action.payload;
      if (action.payload) {
        // Remember guest checkout for the active token so a reload keeps it.
        persistGuestCheckout(getStorage("session", "tkn") || getCurrentCheckoutToken());
        localStorage.removeItem("shopperRef");
        if (state.shopperinfo) {
          state.shopperinfo.shopperRef = "";
        }
      } else {
        clearGuestCheckout();
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginOtpInitiation.pending, (state) => {
        state.otpInitiated = true;
        state.loginOtpVerificationErrMsg = "";
      })
      .addCase(loginOtpInitiation.fulfilled, (state) => {
        state.showOtpModal = true;
        state.otpInitiated = false;
      })
      .addCase(loginOtpInitiation.rejected, (state, action) => {
        state.otpInitiated = false;
        state.showOtpModal = false;
        state.loginOtpInitiationErrorMsg = action.payload?.data?.message;
      })
      .addCase(loginOtpVerification.pending, (state) => {
        state.otpVerificationInitiated = true;
        state.loginOtpInitiationErrorMsg = "";
      })
      .addCase(loginOtpVerification.fulfilled, (state, action) => {
        state.otpVerificationInitiated = false;
        state.loginOtpVerificationErrMsg = "";
        state.shopperinfo = action.payload;
        state.isLoggedIn = true;
        state.guestCheckout = false;
        clearGuestCheckout();
        state.showOtpModal = false;
        if (action?.payload?.sessionToken) {
          localStorage.setItem("sessionToken", action?.payload?.sessionToken);
          localStorage.setItem("isUserLoggedIn", true);
          if (action?.payload?.shopperRef) {
            localStorage.setItem("shopperRef", action.payload.shopperRef);
          }
        } else {
          localStorage.removeItem("shopperRef");
        }
      })
      .addCase(loginOtpVerification.rejected, (state, action) => {
        state.otpVerificationInitiated = false;
        if (action?.payload?.status === 429) {
          state.showOtpModal = false;
          state.loginOtpInitiationErrorMsg = action.payload?.data?.message;
        } else {
          state.loginOtpVerificationErrMsg = action.payload?.data?.message;
        }
      });
  },
});

// Entry point for session load: syncs login state, and when a logged-in shopper's session
// no longer has login enabled, drops the shopper's saved address/instruments so checkout
// falls back to the address from the get-session response.
export const syncShopperLogin = (sessionDetails) => (dispatch) => {
  const hadShopper = hasShopperToken();
  dispatch(syncLoginState(sessionDetails));
  if (hadShopper && !getCookieValue("shopper_token") && sessionDetails?.configs && !isLoginEnabled(sessionDetails)) {
    dispatch(updateSavedAddressData({}));
    dispatch(removeAllSavedAddress());
    dispatch(removeUpdatedShopperDetails());
    dispatch(openAddressComponent());
    dispatch(setSavedInstruments([]));
    sessionStorage.removeItem("addressCollapse");
  }
};

export const persistedShopperInfoSliceReducer = persistReducer(
  persistConfig,
  shopperInfoSlice.reducer
);

export const {
  syncLoginState,
  resetOtpModal,
  resetOtpInitiationError,
  resetOtpValidationError,
  resetShopperInfo,
  handleKeepSignIn,
  handleGuestCheckout,
} = shopperInfoSlice.actions;

export const selectMobileNumber = (state) => state?.shopperInfo?.shopperinfo?.shopperRef;

export const selectSessionToken = (state) =>
  state?.shopperInfo?.shopperinfo?.sessionToken || getCookieValue('shopper_token') || localStorage.getItem('sessionToken');

// Logout is only for shoppers who logged in themselves (OTP), not cookie logins.
export const selectShowLogout = (state) =>
  !!state?.shopperInfo?.isLoggedIn && !getCookieValue("shopper_token");

export const selectGuestCheckout = (state) => state?.shopperInfo?.guestCheckout;

export const selectLoginEnabled = (state) =>
  isLoginEnabled(state?.paymentSessionDetails?.sessionDetails);

// True while the login screen must be shown and the rest of checkout stays locked.
export const selectShowLogin = (state) =>
  selectLoginEnabled(state) &&
  !state?.shopperInfo?.isLoggedIn &&
  !state?.shopperInfo?.guestCheckout;

// Guest who chose guest checkout on a login-enabled session (can still go back to login).
export const selectIsGuestLoggedIn = (state) =>
  selectLoginEnabled(state) && !!state?.shopperInfo?.guestCheckout;

export default shopperInfoSlice.reducer;
